/**
 * Auth context — the dashboard's session state machine.
 *
 * Holds the authenticated `Principal` (+ the safe `User` projection from login)
 * and exposes `login` / `logout`. On mount, if a token is already persisted, it
 * hydrates the session by calling `GET /auth/me`. Honors the provider-agnostic
 * contract: regardless of the backend `AuthProvider` (jwt/google/supabase), the
 * dashboard sends `Authorization: Bearer` and resolves the same `Principal`.
 *
 * The `HotlineClient` is injectable (prop) so tests drive a mocked client.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import type { HotlineClient } from '@hotline/client-core';
import type {
  AuthResponse,
  LoginRequest,
  Principal,
  RegisterRequest,
  User,
} from '@hotline/contracts';

import { client as defaultClient, onAuthError } from '../client.js';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: AuthStatus;
  principal: Principal | null;
  user: User | null;
  client: HotlineClient;
  login: (credentials: LoginRequest) => Promise<void>;
  /** Self-register (creates a new org + org-admin) and start the session. */
  register: (body: RegisterRequest) => Promise<void>;
  /** Sign in with a Google Identity Services ID token (`credential`). */
  loginWithGoogle: (credential: string) => Promise<void>;
  /** Request a passwordless magic-link email (does not change session state). */
  requestMagicLink: (email: string, redirectPath?: string) => Promise<void>;
  /** Consume a magic-link token from the emailed URL → authenticated session. */
  verifyMagicLink: (token: string) => Promise<void>;
  logout: () => void;
  /** Convenience role checks. */
  isMetaAdmin: boolean;
  isOrgAdmin: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  /** Injectable client (defaults to the app singleton). */
  client?: HotlineClient;
  /** Skip the initial /auth/me hydration (tests that set state directly). */
  skipHydration?: boolean;
  /** Seed an already-authenticated session (tests). Implies skipHydration. */
  initialPrincipal?: Principal | null;
  /** Seed the safe user projection alongside `initialPrincipal` (tests). */
  initialUser?: User | null;
}

export function AuthProvider({
  children,
  client = defaultClient,
  skipHydration = false,
  initialPrincipal = null,
  initialUser = null,
}: AuthProviderProps): ReactElement {
  const seeded = initialPrincipal != null;
  const [status, setStatus] = useState<AuthStatus>(
    seeded ? 'authenticated' : skipHydration ? 'anonymous' : 'loading',
  );
  const [principal, setPrincipal] = useState<Principal | null>(initialPrincipal);
  const [user, setUser] = useState<User | null>(initialUser);
  const mounted = useRef(true);

  // Hydrate from a persisted token on first mount.
  useEffect(() => {
    mounted.current = true;
    if (skipHydration || seeded) return;
    if (!client.isAuthenticated) {
      setStatus('anonymous');
      return;
    }
    client.api
      .me()
      .then(({ principal: p, user: u }) => {
        if (!mounted.current) return;
        // Set BOTH halves, exactly as a fresh login does. Restoring only the
        // principal left `user` null, so every reloaded session rendered the
        // raw user id where a name belongs.
        setUser(u);
        setPrincipal(p);
        setStatus('authenticated');
      })
      .catch(() => {
        if (!mounted.current) return;
        client.logout();
        setPrincipal(null);
        setUser(null);
        setStatus('anonymous');
      });
    return () => {
      mounted.current = false;
    };
  }, [client, skipHydration]);

  // React to an unrecoverable refresh failure anywhere in the app.
  useEffect(() => {
    return onAuthError(() => {
      if (!mounted.current) return;
      setPrincipal(null);
      setUser(null);
      setStatus('anonymous');
    });
  }, []);

  // Apply a freshly-issued session (login / Google / magic-link all funnel here).
  const applySession = useCallback((res: AuthResponse) => {
    setUser(res.user);
    setPrincipal({
      userId: res.user.id,
      role: res.user.role,
      orgId: res.user.organizationId ?? null,
      projectId: res.user.widgetInstanceId ?? null,
    });
    setStatus('authenticated');
  }, []);

  const login = useCallback(
    async (credentials: LoginRequest) => {
      applySession(await client.api.login(credentials));
    },
    [client, applySession],
  );

  const register = useCallback(
    async (body: RegisterRequest) => {
      applySession(await client.api.register(body));
    },
    [client, applySession],
  );

  const loginWithGoogle = useCallback(
    async (credential: string) => {
      applySession(await client.api.loginWithGoogle(credential));
    },
    [client, applySession],
  );

  const requestMagicLink = useCallback(
    async (email: string, redirectPath?: string) => {
      await client.api.requestMagicLink({ email, ...(redirectPath ? { redirectPath } : {}) });
    },
    [client],
  );

  const verifyMagicLink = useCallback(
    async (token: string) => {
      applySession(await client.api.verifyMagicLink(token));
    },
    [client, applySession],
  );

  const logout = useCallback(() => {
    client.logout();
    setPrincipal(null);
    setUser(null);
    setStatus('anonymous');
  }, [client]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      principal,
      user,
      client,
      login,
      register,
      loginWithGoogle,
      requestMagicLink,
      verifyMagicLink,
      logout,
      isMetaAdmin: principal?.role === 'META_ADMIN',
      isOrgAdmin: principal?.role === 'ORG_ADMIN',
    }),
    [status, principal, user, client, login, register, loginWithGoogle, requestMagicLink, verifyMagicLink, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Access the auth context; throws if used outside an `AuthProvider`. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an <AuthProvider>');
  }
  return ctx;
}
